// 업적 달성 — 서버 이관 (2026-06-19 근본 봉쇄·웹 lib/achievements.js 동기)
//
// checkAchievements(action) → claim_achievements_for_action RPC
// - 클라는 action만 전달. 조건 판정·보상(EXP/BULL)은 서버가 원천 테이블 재조회로 결정 → 위조 차단.
// - user_achievements 직접 INSERT 폐지(06 REVOKE), addExp 클라 호출 폐지(RPC가 처리).
// - 반환: 새로 해금된 업적 상세 배열

export type Achievement = {
  code: string;
  name: string;
  icon?: string | null;
  rarity?: string | null;
  exp_reward?: number;
  bull_reward?: number;
};

export type AchievementAction =
  | "post"
  | "comment"
  | "attendance"
  | "level_up"
  | "onboarding"
  | "vote"
  | "dm_sent"
  | "tab_explorer"
  | "profile_update"
  | "like_received";

/** like_received는 타인 대상 → 서버 grant_like_exp 경로. 클라 claim 스킵 규칙. */
export function shouldSkipClaim(action: string): boolean {
  return action === "like_received";
}

/**
 * 서버 응답에 실려 온 신규 해금 목록을 안전하게 꺼낸다 (2026-09-15 출석 업적 누락 수정).
 * - /api/mobile/scrap/attend 는 서버가 claim 한 결과를 `achievements`로 돌려준다.
 * - 구버전 서버(필드 없음)·깨진 응답이면 빈 배열 — 토스트만 안 뜨고 본 행동(출석)은 그대로 성공.
 * - name 없는 항목은 토스트 제목을 그릴 수 없으므로 버린다.
 */
export function achievementsFromResponse(value: unknown): Achievement[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (a): a is Achievement =>
      !!a && typeof a === "object" && typeof (a as Record<string, unknown>).code === "string" && typeof (a as Record<string, unknown>).name === "string"
  );
}

/**
 * 탭 탐험가(tab_explorer) 방문 기록 — 순수 함수 (2026-09-15 모바일 경로 신설).
 * 웹 TabTracker와 같은 규칙: 메인 탭을 전부 한 번씩 방문하면 complete.
 * - allTabs에 없는 값(null·하위 화면 판정 실패)은 기록하지 않는다.
 * - 저장소에서 읽은 값이 오염돼 있어도(배열 아님·모르는 탭) allTabs 기준으로 걸러 다시 센다.
 * @returns visited 갱신본 · changed(저장 필요 여부) · complete(전 탭 방문 여부)
 */
export function recordTabVisit(
  visited: unknown,
  tab: string | null | undefined,
  allTabs: readonly string[]
): { visited: string[]; changed: boolean; complete: boolean } {
  const known = Array.isArray(visited) ? visited.filter((t): t is string => allTabs.includes(t as string)) : [];
  const unique = Array.from(new Set(known));
  const isNew = !!tab && allTabs.includes(tab) && !unique.includes(tab);
  const next = isNew ? [...unique, tab as string] : unique;
  return {
    visited: next,
    // 오염값을 걸러낸 경우도 저장 대상 — 다음 읽기가 깨끗해진다
    changed: isNew || !Array.isArray(visited) || next.length !== (visited as unknown[]).length,
    complete: allTabs.every((t) => next.includes(t)),
  };
}
